class ArticleContentEnhancer extends HTMLElement {
  connectedCallback() {
    this.#enhance();
  }

  #enhance() {
    const prose = this.querySelector("[data-article-prose]");

    if (!prose) {
      return;
    }

    const referenceHeading = this.#findReferencesHeading(prose);
    let authorNode = null;

    if (referenceHeading) {
      const nodes = this.#collectFollowingNodes(referenceHeading);

      if (nodes.length > 0 && this.#isAuthorNode(nodes[nodes.length - 1])) {
        authorNode = nodes.pop();
      }

      referenceHeading.remove();

      if (nodes.length > 0) {
        this.#renderReferencesAccordion(nodes);
      }
    } else {
      const paragraphs = prose.querySelectorAll("p");
      const lastParagraph = paragraphs[paragraphs.length - 1];

      if (lastParagraph && this.#isAuthorNode(lastParagraph)) {
        authorNode = lastParagraph;
      }
    }

    if (authorNode) {
      if (this.dataset.showAuthor !== "false") {
        this.#renderAuthorAccordion(authorNode);
      }

      authorNode.remove();
    }

    if (this.querySelector(".article-author-card")) {
      this.closest(".article-page")?.classList.add("has-extracted-author");
    }
  }

  #findReferencesHeading(container) {
    const candidates = container.querySelectorAll("h2, h3, h4, h5, h6, p, strong");

    for (const candidate of candidates) {
      if (this.#isReferencesHeading(candidate)) {
        return candidate.closest("p") || candidate;
      }
    }

    return null;
  }

  #isReferencesHeading(node) {
    const text = node.textContent.replace(/\s+/g, " ").trim().toLowerCase();
    return /^(references|reference|sources):?$/.test(text);
  }

  #collectFollowingNodes(startNode) {
    const nodes = [];
    let current = startNode.nextElementSibling;

    while (current) {
      nodes.push(current);
      current = current.nextElementSibling;
    }

    return nodes;
  }

  #isAuthorNode(node) {
    if (!node || !["P", "DIV"].includes(node.tagName)) {
      return false;
    }

    const text = node.textContent.replace(/\s+/g, " ").trim();

    if (!text || text.length > 120) {
      return false;
    }

    if (/https?:\/\//i.test(text) || /^\d+[\).]/.test(text)) {
      return false;
    }

    if (node.querySelector("a, ul, ol, img, iframe")) {
      return false;
    }

    const credentialPattern = /^[A-Z][\w.'-]*(?:\s+[A-Z][\w.'-]*){0,4}(?:,\s*(?:[A-Z]{2,}(?:\/[A-Z]{2,})*|[A-Z][a-z]+(?:\s+[A-Z][a-z]+)*)(?:,\s*(?:[A-Z]{2,}(?:\/[A-Z]{2,})*|[A-Z][a-z]+(?:\s+[A-Z][a-z]+)*))*)?$/;
    return credentialPattern.test(text);
  }

  #parseAuthor(text) {
    const parts = text.split(",").map((part) => part.trim()).filter(Boolean);

    if (parts.length < 2) {
      return { name: text, credentials: "" };
    }

    const credentials = parts.pop();
    return {
      name: parts.join(", "),
      credentials
    };
  }

  #getAccordionsContainer() {
    let container = this.querySelector(".article-footer-accordions");

    if (!container) {
      container = document.createElement("div");
      container.className = "article-footer-accordions";
      this.appendChild(container);
    }

    return container;
  }

  #createAccordion(title, contentNode, ariaLabel) {
    const details = document.createElement("details", { is: "accordion-disclosure" });
    details.className = "accordion article-footer-accordion__panel group";
    details.setAttribute("aria-expanded", "false");
    details.setAttribute("aria-label", ariaLabel);

    details.innerHTML = `
      <summary>
        <div class="accordion__toggle bold">
          <span class="h6 article-footer-accordion__title"></span>
          <span class="circle-chevron group-hover:colors group-expanded:colors group-expanded:rotate">${this.#chevronIcon()}</span>
        </div>
      </summary>
      <div class="accordion__content">
        <div class="article-footer-accordion__content"></div>
      </div>
    `;

    details.querySelector(".article-footer-accordion__title").textContent = title;
    details.querySelector(".article-footer-accordion__content").appendChild(contentNode);

    return details;
  }

  #renderReferencesAccordion(nodes) {
    const referencesBody = document.createElement("div");
    referencesBody.className = "article-references__body prose";
    nodes.forEach((node) => referencesBody.appendChild(node));

    const accordion = this.#createAccordion(
      this.dataset.referencesLabel || "References",
      referencesBody,
      this.dataset.referencesLabel || "References"
    );

    this.#getAccordionsContainer().appendChild(accordion);
  }

  #renderAuthorAccordion(node) {
    const authorCard = this.#buildAuthorCard(node);
    const { name } = this.#parseAuthor(node.textContent.replace(/\s+/g, " ").trim());
    const title = name || this.dataset.authorLabel || "Author";

    const accordion = this.#createAccordion(title, authorCard, this.dataset.authorLabel || "Author");
    this.#getAccordionsContainer().appendChild(accordion);
  }

  #buildAuthorCard(node) {
    const text = node.textContent.replace(/\s+/g, " ").trim();
    const { name, credentials } = this.#parseAuthor(text);
    const authorCard = document.createElement("div");
    authorCard.className = "article-author-card";

    authorCard.innerHTML = `
      <div class="article-author-card__icon" aria-hidden="true">
        ${this.#authorIcon()}
      </div>
      <div class="article-author-card__content v-stack gap-2">
        <span class="blog-meta-pill">${this.dataset.authorLabel || "Author"}</span>
        <p class="article-author-card__name h5"></p>
      </div>
    `;

    authorCard.querySelector(".article-author-card__name").textContent = name;

    if (credentials) {
      const credentialsNode = document.createElement("p");
      credentialsNode.className = "article-author-card__credentials";
      credentialsNode.textContent = credentials;
      authorCard.querySelector(".article-author-card__content").appendChild(credentialsNode);
    }

    return authorCard;
  }

  #chevronIcon() {
    return `
      <svg role="presentation" focusable="false" width="8" height="6" viewBox="0 0 8 6">
        <path d="m1 1.5 3 3 3-3" fill="none" stroke="currentColor" stroke-width="1.5"></path>
      </svg>
    `;
  }

  #authorIcon() {
    return `
      <svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" stroke-width="1.75">
        <circle cx="12" cy="8" r="4"></circle>
        <path d="M5 20c1.5-3 4-4.5 7-4.5s5.5 1.5 7 4.5"></path>
      </svg>
    `;
  }
}

if (!window.customElements.get("article-content-enhancer")) {
  window.customElements.define("article-content-enhancer", ArticleContentEnhancer);
}
